import { useMemo, useState } from 'react';

/**
 * Ordenação clicável das listagens.
 *
 * Um utilitário só, e não uma cópia por tabela: a regra de desempate e o
 * tratamento do vazio precisam ser os mesmos em Fornecedores, RNCs e
 * Certificações, senão cada tela ordena de um jeito e a pessoa desconfia da que
 * está olhando.
 *
 * Ordena **em memória**, e isso é uma decisão de escala, não preguiça: as
 * listagens carregam a carteira inteira (~47 lojas, algumas dezenas de RNCs) em
 * uma requisição. Mandar a ordenação para o servidor exigiria paginação, e
 * paginar 47 linhas seria construir infraestrutura para um problema que não
 * existe. Se um dia a lista passar de alguns milhares, isto aqui vira parâmetro
 * de rota — e a assinatura já está no formato certo para isso.
 */

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

/** O que se extrai de uma linha para comparar. `null` = sem valor. */
export type SortAccessor<T> = (row: T) => string | number | boolean | null | undefined;

/**
 * Compara dois valores da mesma coluna.
 *
 * **Vazio vai sempre para o fim**, nas duas direções. É deliberado: numa
 * listagem ordenada por "última avaliação", inverter a direção não pode encher
 * a primeira tela de traços — quem inverteu queria ver as mais antigas, não as
 * que não têm nenhuma.
 *
 * Texto compara em `pt-BR` para "Ávila" cair junto de "Avila", e não depois de
 * "Zorzi" como o `<` de JavaScript faria.
 */
function compare(a: ReturnType<SortAccessor<unknown>>, b: ReturnType<SortAccessor<unknown>>): number {
  const vazioA = a === null || a === undefined || a === '';
  const vazioB = b === null || b === undefined || b === '';
  if (vazioA && vazioB) return 0;
  if (vazioA) return 1;
  if (vazioB) return -1;

  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b, 'pt-BR');
  return Number(a) - Number(b);
}

export interface TableSort<T, K extends string> {
  rows: T[];
  sort: SortState<K>;
  /** Clicar na mesma coluna inverte; em outra, começa ascendente. */
  toggle: (key: K) => void;
}

/**
 * `NoInfer` no estado inicial é o que faz `K` sair das **colunas**, e não da
 * coluna inicial. Sem ele, `{ key: 'nome' }` estreita `K` para `'nome'` e todo
 * cabeçalho seguinte vira erro de tipo — o compilador acusaria a chamada certa.
 */
export function useTableSort<T, K extends string>(
  rows: T[],
  accessors: Record<K, SortAccessor<T>>,
  initial: SortState<NoInfer<K>>,
): TableSort<T, K> {
  const [sort, setSort] = useState<SortState<K>>(initial);

  const sorted = useMemo(() => {
    const accessor = accessors[sort.key];
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const diff = compare(accessor(a), accessor(b));
      // O vazio ignora a direção: ele sai do `compare` já com o sinal certo, e
      // multiplicar pelo fator o traria de volta para o topo no descendente.
      if (diff !== 0 && (accessor(a) == null || accessor(b) == null)) return diff;
      return diff * factor;
    });
    // `accessors` fica **fora** das dependências de propósito: o chamador o
    // recria a cada render, e incluí-lo reordenaria a lista a cada tecla
    // digitada num filtro. Ele é um mapa de funções puras sobre a linha — o que
    // muda de verdade é `rows`, que está aqui.
  }, [rows, sort.key, sort.direction]);

  return {
    rows: sorted,
    sort,
    toggle: (key: K) =>
      setSort((atual) =>
        atual.key === key
          ? { key, direction: atual.direction === 'asc' ? 'desc' : 'asc' }
          : { key, direction: 'asc' },
      ),
  };
}
